import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  createFilterOptions,
  Divider,
  FormControlLabel,
  FormHelperText,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import React, { forwardRef, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { BookingApi } from "../../API/Apis/BookingApi";
import BookingGetters from "../../store/onlineBooking/getters";
import { useForm, Controller, useWatch } from "react-hook-form";
import { FormData } from "../../@types/DTO/NewWebCheckTypes/FormTypes";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  it as itLocale,
  es as esLocale,
  fr as frLocale,
  de as deLocale,
} from "date-fns/locale";
import { itIT, esES, frFR, deDE } from "@mui/x-date-pickers/locales";
import PhoneInput from "react-phone-input-2";
import WebCheckInLayout from "./WebCheckInLayout";
import { calculateNestedProgress } from "../../helpers/calculateNestedProgress";
import WebCheckInPopUp from "./WebCheckInPopUp";
import i18n from "../../config/i18n";
import AppCountrySelect from "../../components/Layouts/AppCountrySelect";

type Props = {
  details: any;
  onBack: () => void;
  onNext?: (data: any) => void;
  isSmallScreen: boolean;
  stepGroups?: any;
  reservationDetails: any;
  progressCalculator?: (
    currentSection: number,
    totalSections: number
  ) => number;
  isShareboxEnabled?: boolean;
  onWideChange?: (isWide: boolean) => void;
};

// eslint-disable-next-line react/display-name
const RHFPhoneInput = forwardRef(({ value, onChange, ...props }: any, ref) => (
  <PhoneInput
    {...props}
    value={value}
    onChange={onChange}
    inputProps={{ ref }}
    containerStyle={{
      marginBottom: "15px",
    }}
    inputStyle={{
      width: "100%",
      height: "56px",
      borderRadius: "8px",
      fontSize: "14px",
      borderColor: "#19344FCC",
      borderWidth: "2px",
      boxShadow: "none",
    }}
    buttonStyle={{
      border: "none",
    }}
    specialLabel=""
  />
));

export default function BillingDetailsForm({
  details,
  onBack,
  onNext,
  isSmallScreen,
  stepGroups,
  reservationDetails,
  progressCalculator,
  isShareboxEnabled,
  onWideChange,
}: Props) {
  const lang = i18n.language;
  const dateLocale =
    lang === "it"
      ? itLocale
      : lang === "es"
      ? esLocale
      : lang === "fr"
      ? frLocale
      : lang === "de"
      ? deLocale
      : undefined;
  const muiLocaleText =
    lang === "it"
      ? itIT.components.MuiLocalizationProvider.defaultProps.localeText
      : lang === "es"
      ? esES.components.MuiLocalizationProvider.defaultProps.localeText
      : lang === "fr"
      ? frFR.components.MuiLocalizationProvider.defaultProps.localeText
      : lang === "de"
      ? deDE.components.MuiLocalizationProvider.defaultProps.localeText
      : undefined;
  const { t: translate } = useTranslation();
  const prefixFilterOptions = createFilterOptions<string>({
    limit: 50,
    matchFrom: "start",
  });
  const [currentSection, setCurrentSection] = useState<number>(1);
  useEffect(() => {
    onWideChange?.(currentSection === 5);
  }, [currentSection, onWideChange]);
  const [countries, setCountries] = useState<string[]>([]);
  const [states, setStates] = useState<string[]>([]);
  const [cities, setCities] = useState<string[]>([]);
  const [birthCities, setBirthCities] = useState<string[]>([]);
  const [birthStates, setBirthStates] = useState<string[]>([]);
  const [zipCode, setZipCode] = useState<string[]>([]);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [modalMessage, setModalMessage] = useState<string>("");
  const [isBlacklistedText, setIsBlacklistedText] = useState<string>("");
  const [existingCustomerData, setExistingCustomerData] = useState<any>(null);
  const [isLoadingCustomerData, setIsLoadingCustomerData] =
    useState<boolean>(false);
  const [selectedProfile, setSelectedProfile] = useState<"new" | "existing">(
    "new"
  );
  const [newProfileSnapshot, setNewProfileSnapshot] = useState<any>(null);

  const appSettings = BookingGetters.getAppSettings();
  const webCheckinSecondaryColor = appSettings?.webCheckinSecondaryColor;
  const webCheckinHeaderBgColor = appSettings?.webCheckinHeaderBgColor;
  const enableUppercaseInputField = appSettings?.enableUppercaseInputField;
  const country = appSettings?.config?.country;

  const requiredConfig: Record<string, boolean | undefined> = {
    phoneNumber: appSettings?.driverPhoneNoRequired,
    email: appSettings?.driverEmailRequired,
    streetName: appSettings?.driverStreetNameRequired,
    streetNumber: appSettings?.driverStreetNoRequired,
    country: appSettings?.driverCountryRequired,
    state: appSettings?.driverStateRequired,
    city: appSettings?.driverCityRequired,
    zipCode: appSettings?.driverZipCodeRequired,
    birthDate: appSettings?.driverBirthDateRequired,
    birthNation: appSettings?.driverBirthNationRequired,
    birthPlace: appSettings?.driverBirthPlaceRequired,
    birthProvince: appSettings?.driverBirthProvRequired,
    sex: appSettings?.driverGenderRequired,
    ssnNumber: appSettings?.driverSSNRequired,
  };

  const getRules = (fieldName: string, label: string) => {
    return requiredConfig[fieldName]
      ? { required: `${translate(label)} ${translate("is required")}` }
      : {};
  };
  const isMountedRef = useRef(true);
  const prevSsnFetchKeyRef = useRef<string>("");

  const textResource = BookingGetters.getTextResourceDetailsBasedOnLang();
  const data = textResource?.[lang]?.flatMap((item) => item.data || []) || [];

  const webCheckInRadioGroup = {
    marginBottom: isSmallScreen ? "15px" : "30px",
  };

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await BookingApi.getAllCountries(country);
        if (isMountedRef.current) {
          setCountries(response.data);
        }
      } catch (error) {
        console.error("Error fetching countries:", error);
      }
    };

    fetchCountries();
  }, [country]);

  const totalSections = 5;
  const nestedProgress = progressCalculator
    ? progressCalculator(currentSection, totalSections)
    : calculateNestedProgress({
        currentPage: "BillingDetails",
        currentSection,
        totalSections,
        stepGroups,
      });

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    clearErrors,
    watch,
    trigger,
  } = useForm<FormData>({
    defaultValues: {
      firstName: details?.firstName || "",
      lastName: details?.lastName || "",
      phNum1: details?.phNum1 || "",
      email: details?.email || "",
      address: details?.streetName || "",
      streetNumber: details?.streetNumber || "",
      country: details?.country || "",
      city: details?.city || "",
      state: details?.state || "",
      zip: details?.zip || "",
      dateOfBirth: details?.dateOfBirth
        ? typeof details.dateOfBirth === "string"
          ? details.dateOfBirth.split("T")[0]
          : new Date(details.dateOfBirth).toISOString().split("T")[0]
        : "",
      birthNation: details?.birthNation || "",
      birthPlace: details?.birthPlace || "",
      birthProvince: details?.birthProvince || "",
      sex: details?.sex || "",
      ssnNumber: details?.ssnNumber || "",
      companyName: details?.companyName || "",
      vatNumber: details?.vatNumber || "",
      issuedBy: details?.issueBy || "",
      issueBy1: details?.issueBy1 || "",
      issueBy: "",
    },
  });
  const watchedCountry = watch("country");
  const [isInvoiceAlreadyCreated, setIsInvoiceAlreadyCreated] =
    useState<boolean>(false);
  useEffect(() => {
    const controller = new AbortController();
    const checkInvoice = async () => {
      try {
        const isItalyAddress =
          watchedCountry?.toUpperCase() === "ITALIA" ||
          watchedCountry?.toUpperCase() === "ITALY";
        const vatNumberOrTaxCode =
          details?.customerType === "COMPANY"
            ? details?.vatNumber || ""
            : details?.ssnNumber || "";
        if (!vatNumberOrTaxCode || !isItalyAddress) return;
        const response = await BookingApi.getExistingCustomerRAInv(
          vatNumberOrTaxCode
        );
        if (!controller.signal.aborted) {
          setIsInvoiceAlreadyCreated(
            response.data?.data?.isInvoiceOrRa === "Y"
          );
        }
      } catch (error) {
        if (!controller.signal.aborted) {
          console.error("Error checking invoice:", error);
          setIsInvoiceAlreadyCreated(false);
        }
      }
    };
    checkInvoice();
    return () => {
      controller.abort();
    };
  }, [
    details?.vatNumber,
    details?.ssnNumber,
    watchedCountry,
    details?.customerType,
  ]);

  const watchedValues = useWatch({
    control,
    name: [
      "firstName",
      "lastName",
      "birthNation",
      "birthPlace",
      "sex",
      "dateOfBirth",
      "country",
      "city",
    ],
  });

  const showErrorModal = (message: string): void => {
    setModalMessage(message);
    setOpenModal(true);
  };

  useEffect(() => {
    const fetchSSN = async () => {
      if (
        watchedValues[0] && // firstName
        watchedValues[1] && // lastName
        watchedValues[2] && // gender
        watchedValues[3] && // birthDate
        watchedValues[4] && // birthCity
        watchedValues[5] && // province
        watchedValues[6] // country
      ) {
        const fetchKey = JSON.stringify((watchedValues as any[]).slice(0, 7));
        if (prevSsnFetchKeyRef.current === fetchKey) return;
        prevSsnFetchKeyRef.current = fetchKey;

        const selectedCountry = watchedValues[6]?.toUpperCase();
        if (selectedCountry.startsWith("ITAL")) {
          try {
            const response = await BookingApi.getSsnByCustomerDetails(
              watchedValues[0],
              watchedValues[1],
              watchedValues[2],
              watchedValues[3],
              watchedValues[4],
              watchedValues[5] as any,
              watchedValues[6]
            );

            if (isMountedRef.current) {
              const ssnValue = String(response?.data ?? "");
              setValue(
                "ssnNumber",
                ssnValue === "100000000000000000"
                  ? "9999999999999999"
                  : ssnValue,
                { shouldValidate: true }
              );
            }
          } catch (error) {
            console.error("Error fetching SSN:", error);
          }
        } else if (isMountedRef.current) {
          setValue("ssnNumber", "9999999999999999", {
            shouldValidate: true,
          });
        }
      }
    };

    if (currentSection === 3 || currentSection === 4) {
      fetchSSN();
    }
  }, [watchedValues, setValue, countries]);

  // When user picks "existing" profile in section 5, populate form with that data.
  // When they switch back to "new", restore the original details.
  useEffect(() => {
    const uc = (val: string) =>
      enableUppercaseInputField ? (val || "").toUpperCase() : val || "";
    if (selectedProfile === "existing" && existingCustomerData?.customers) {
      const c = existingCustomerData.customers;
      setValue("firstName", uc(c.firstName || ""), { shouldValidate: false });
      setValue("lastName", uc(c.lastName || ""), { shouldValidate: false });
      setValue("phNum1", c.telephone || c.phoneNumber || c.mobile || "", {
        shouldValidate: false,
      });
      setValue("email", uc(c.email || c.email_invoice || ""), {
        shouldValidate: false,
      });
      setValue("address", uc(c.street || ""), { shouldValidate: false });
      setValue("streetNumber", uc(c.addressNumber || c.n || ""), {
        shouldValidate: false,
      });
      setValue("country", c.country || "", { shouldValidate: false });
      setValue("city", uc(c.city || ""), { shouldValidate: false });
      setValue("state", uc(c.province || c.state || ""), {
        shouldValidate: false,
      });
      setValue("zip", uc(c.postalCode || c.zip || ""), {
        shouldValidate: false,
      });
      setValue("dateOfBirth", c.dob ? c.dob.split("T")[0] : "", {
        shouldValidate: false,
      });
      setValue("birthNation", c.birthNation || c.cob || "", {
        shouldValidate: false,
      });
      setValue("birthPlace", uc(c.birthplace || ""), { shouldValidate: false });
      setValue("birthProvince", uc(c.birthProv || ""), {
        shouldValidate: false,
      });
      if (c.sex !== undefined && c.sex !== null) {
        setValue("sex", c.sex === true ? "MALE" : "FEMALE", {
          shouldValidate: false,
        });
      }
      setValue("ssnNumber", uc(c.idNumber || c.codiceFiscale || ""), {
        shouldValidate: false,
      });
      setValue("companyName", uc(c.companyName || c.company || ""), {
        shouldValidate: false,
      });
      setValue("vatNumber", uc(c.vat || c.vatNumber || ""), {
        shouldValidate: false,
      });
    } else if (selectedProfile === "new") {
      setValue("firstName", uc(details?.firstName || ""), {
        shouldValidate: false,
      });
      setValue("lastName", uc(details?.lastName || ""), {
        shouldValidate: false,
      });
      setValue("phNum1", details?.phNum1 || "", { shouldValidate: false });
      setValue("email", uc(details?.email || ""), { shouldValidate: false });
      setValue("address", uc(details?.streetName || ""), {
        shouldValidate: false,
      });
      setValue("streetNumber", uc(details?.streetNumber || ""), {
        shouldValidate: false,
      });
      setValue("country", details?.country || "", { shouldValidate: false });
      setValue("city", uc(details?.city || ""), { shouldValidate: false });
      setValue("state", uc(details?.state || ""), { shouldValidate: false });
      setValue("zip", uc(details?.zip || ""), { shouldValidate: false });
      setValue(
        "dateOfBirth",
        details?.dateOfBirth
          ? typeof details.dateOfBirth === "string"
            ? details.dateOfBirth.split("T")[0]
            : new Date(details.dateOfBirth).toISOString().split("T")[0]
          : "",
        { shouldValidate: false }
      );
      setValue("birthNation", details?.birthNation || "", {
        shouldValidate: false,
      });
      setValue("birthPlace", uc(details?.birthPlace || ""), {
        shouldValidate: false,
      });
      setValue("birthProvince", uc(details?.birthProvince || ""), {
        shouldValidate: false,
      });
      setValue("sex", details?.sex || "", { shouldValidate: false });
      setValue("ssnNumber", uc(details?.ssnNumber || ""), {
        shouldValidate: false,
      });
      setValue("companyName", uc(details?.companyName || ""), {
        shouldValidate: false,
      });
      setValue("vatNumber", uc(details?.vatNumber || ""), {
        shouldValidate: false,
      });
    }
  }, [selectedProfile, existingCustomerData]);

  useEffect(() => {
    const blacklistedTextData = data.find(
      (item) => item.key === "customerBlacklisted"
    );
    setIsBlacklistedText(
      blacklistedTextData?.body || translate("customerBlacklisted")
    );
  }, [lang, textResource, translate]);

  // Check the current values from the form state
  const currentCountry = watch("country")?.toUpperCase();

  // Disable if NOT Italian
  const isNotItalian =
    currentCountry !== "ITALIA" && currentCountry !== "ITALY";

  const hasExistingCustomer = existingCustomerData?.customers != null;

  const handleContinue = async (data: FormData): Promise<void> => {
    // At section 2 (Italian, COMPANY): only call API if billing fields have changed
    if (
      currentSection === 2 &&
      details?.customerType === "COMPANY" &&
      !isNotItalian
    ) {
      const norm = (v: any) => String(v ?? "").trim();
      const companyHasChanges =
        norm((data as any).companyName) !== norm(details?.companyName) ||
        norm((data as any).vatNumber) !== norm(details?.vatNumber) ||
        norm((data as any).email) !== norm(details?.email) ||
        norm((data as any).streetNumber) !== norm(details?.streetNumber) ||
        norm((data as any).zip) !== norm(details?.zip) ||
        norm((data as any).city) !== norm(details?.city) ||
        norm((data as any).state) !== norm(details?.state) ||
        norm((data as any).country) !== norm(details?.country) ||
        norm((data as any).address || (data as any).streetName) !==
          norm(details?.streetName) ||
        norm((data as any).phNum1 || (data as any).telephone) !==
          norm(details?.phNum1);

      const vatNumberOrTaxCode = (data as any).vatNumber || "";
      if (vatNumberOrTaxCode && companyHasChanges) {
        let freshCustomerData: any = null;
        try {
          setIsLoadingCustomerData(true);
          const response = await BookingApi.getExistingCustomerRAInv(
            vatNumberOrTaxCode
          );
          const apiData = response?.data?.data;
          if (apiData?.customers != null) {
            freshCustomerData = apiData;
            setExistingCustomerData(apiData);
          } else {
            setExistingCustomerData(null);
          }
        } catch (error) {
          console.error("existingCustomerInvoice error:", error);
          setExistingCustomerData(null);
        }
        if (freshCustomerData?.customers != null) {
          setNewProfileSnapshot({
            companyName: (data as any).companyName || "",
            vatNumber: (data as any).vatNumber || "",
            phNum1:
              (data as any).telephone ||
              (data as any).mobileNumber ||
              (data as any).phNum1 ||
              "",
            email: (data as any).email || "",
            address: (data as any).streetName || (data as any).address || "",
            streetNumber: (data as any).streetNumber || "",
            zip: (data as any).zip || "",
            city: (data as any).city || "",
            state: (data as any).state || "",
            country: (data as any).country || "",
          });
          setIsLoadingCustomerData(false);
          setCurrentSection(5);
          return;
        }
        setIsLoadingCustomerData(false);
      }
    }

    // At section 4 (Italian, non-company): only call API if billing fields have changed
    if (
      currentSection === 4 &&
      details?.customerType !== "COMPANY" &&
      !isNotItalian
    ) {
      const normDate = (v: any) => {
        if (v == null || v === "") return "";
        const s = String(v);
        return s.includes("T") ? s.split("T")[0] : s;
      };
      const norm = (v: any) => String(v ?? "").trim();
      const individualHasChanges =
        norm((data as any).firstName) !== norm(details?.firstName) ||
        norm((data as any).lastName) !== norm(details?.lastName) ||
        norm((data as any).phNum1) !== norm(details?.phNum1) ||
        norm((data as any).email) !== norm(details?.email) ||
        norm((data as any).streetNumber) !== norm(details?.streetNumber) ||
        norm((data as any).zip) !== norm(details?.zip) ||
        norm((data as any).city) !== norm(details?.city) ||
        norm((data as any).state) !== norm(details?.state) ||
        norm((data as any).country) !== norm(details?.country) ||
        norm((data as any).address || (data as any).streetName) !==
          norm(details?.streetName) ||
        normDate((data as any).dateOfBirth) !==
          normDate(details?.dateOfBirth) ||
        norm((data as any).birthPlace) !== norm(details?.birthPlace) ||
        norm((data as any).birthProvince) !== norm(details?.birthProvince) ||
        norm((data as any).birthNation) !== norm(details?.birthNation) ||
        norm((data as any).sex) !== norm(details?.sex) ||
        norm((data as any).ssnNumber) !== norm(details?.ssnNumber);

      const vatNumberOrTaxCode =
        (data as any).ssnNumber || (data as any).vatNumber || "";
      if (
        vatNumberOrTaxCode &&
        vatNumberOrTaxCode !== "9999999999999999" &&
        individualHasChanges
      ) {
        let freshCustomerData: any = null;
        try {
          setIsLoadingCustomerData(true);
          const response = await BookingApi.getExistingCustomerRAInv(
            vatNumberOrTaxCode
          );
          const apiData = response?.data?.data;
          if (apiData?.customers != null) {
            freshCustomerData = apiData;
            setExistingCustomerData(apiData);
          } else {
            setExistingCustomerData(null);
          }
        } catch (error) {
          console.error("existingCustomerInvoice error:", error);
          setExistingCustomerData(null);
        }
        if (freshCustomerData?.customers != null) {
          setNewProfileSnapshot({
            firstName: (data as any).firstName || "",
            lastName: (data as any).lastName || "",
            phNum1: (data as any).phNum1 || "",
            email: (data as any).email || "",
            address: (data as any).address || "",
            streetNumber: (data as any).streetNumber || "",
            zip: (data as any).zip || "",
            city: (data as any).city || "",
            state: (data as any).state || "",
            country: (data as any).country || "",
            dateOfBirth: (data as any).dateOfBirth || "",
            birthPlace: (data as any).birthPlace || "",
            birthProvince: (data as any).birthProvince || "",
            birthNation: (data as any).birthNation || "",
          });
          setIsLoadingCustomerData(false);
          setCurrentSection(5);
          return;
        }
        setIsLoadingCustomerData(false);
      }
    }

    const isIndividualComplete =
      details?.customerType !== "COMPANY" &&
      (isNotItalian
        ? currentSection === 4
        : hasExistingCustomer
        ? currentSection === 5
        : currentSection === 4);
    const isCompanyComplete =
      details?.customerType === "COMPANY" &&
      (hasExistingCustomer ? currentSection === 5 : currentSection === 2);
    if (isIndividualComplete || isCompanyComplete) {
      if (!onNext) return;
      setIsLoadingCustomerData(true);

      const isExistingProfile =
        selectedProfile === "existing" && existingCustomerData?.customers;
      const isNewProfile =
        selectedProfile === "new" && existingCustomerData?.customers;
      let updatedDetails: any;
      if (isExistingProfile && !isNewProfile) {
        const c = existingCustomerData.customers;
        // Build payload entirely from the existing customer record
        updatedDetails = {
          ...details,
          customerId: details?.customerId || c.customer_id || c.code,
          firstName: c.firstName || "",
          lastName: c.lastName || "",
          phNum1: c.telephone || c.phoneNumber || c.mobile || "",
          email: c.email || c.email_invoice || "",
          streetName: c.street || "",
          streetNumber: c.addressNumber || c.n || "",
          country: c.country || "",
          city: c.city || "",
          state: c.province || c.state || "",
          zip: c.postalCode || c.zip || "",
          dateOfBirth: c.dob ? c.dob.split("T")[0] : "",
          birthNation: c.birthNation || c.cob || "",
          birthPlace: c.birthplace || "",
          birthProvince: c.birthProv || "",
          sex:
            c.sex !== undefined && c.sex !== null
              ? c.sex === true
                ? "MALE"
                : "FEMALE"
              : "",
          ssnNumber: c.idNumber || c.codiceFiscale || "",
          companyName: c.companyName || c.company || "",
        };
      } else if (!isExistingProfile && isNewProfile) {
        updatedDetails = {
          ...details,
          ...newProfileSnapshot,
          streetName:
            newProfileSnapshot?.address || (data as any).streetName || "",
          zip:
            newProfileSnapshot?.zip?.toString() ||
            (data as any).zip?.toString() ||
            "",
        };
      } else {
        updatedDetails = {
          ...details,
          ...data,
          streetName: data.address,
          zip: data.zip?.toString(),
        };
      }
      delete updatedDetails.address;
      delete updatedDetails.issueBy;
      let canProceed = true;
      const restrictBlacklistCustomersFromBooking =
        appSettings?.restrictBlacklistCustomersFromBooking;
      const blacklistId =
        updatedDetails?.ssnNumber || updatedDetails?.vatNumber;

      if (
        blacklistId &&
        restrictBlacklistCustomersFromBooking &&
        !isNotItalian
      ) {
        const blacklistResponse = await BookingApi.checkIfCustomerIsBlacklisted(
          String(blacklistId)
        );
        if (blacklistResponse?.data?.status === "success") {
          showErrorModal(isBlacklistedText);
          canProceed = false;
        }
      }

      if (!canProceed) {
        setIsLoadingCustomerData(false);
        return;
      }
      const isCustomerSameAsDriver1 =
        reservationDetails?.customerId &&
        reservationDetails?.driver1Id &&
        String(reservationDetails.customerId) ===
          String(reservationDetails.driver1Id);
      // If user chose new profile and an existing customer record is present, archive it
      if (!isExistingProfile && hasExistingCustomer) {
        const existingCustomerId =
          existingCustomerData?.customers?.customer_id ||
          existingCustomerData?.customers?.code;
        if (existingCustomerId) {
          try {
            await BookingApi.archiveCustomer(existingCustomerId);
          } catch (error) {
            console.error("Failed to archive existing customer:", error);
          }
        }

        try {
          const insertResponse = await BookingApi.insertNewCustomer(
            reservationDetails.reservationId,
            {
              ...updatedDetails,
              isCustPhysicalPerson: reservationDetails.isCustPhysicalPerson,
              isCustIndividualCompany:
                reservationDetails.isCustIndividualCompany,
            }
          );
          const newCustomerId =
            insertResponse?.data?.customerId ||
            insertResponse?.data?.data?.customerId;
          if (newCustomerId) {
            updatedDetails = { ...updatedDetails, customerId: newCustomerId };
          }
        } catch (error) {
          console.error("Failed to update customer details:", error);
        }
      } else if (
        isNotItalian &&
        details?.customerId &&
        !isCustomerSameAsDriver1
      ) {
        // Foreigner: check if any billing field changed — if so, archive old and create new
        const fieldsToCompare = [
          "firstName",
          "lastName",
          "phNum1",
          "email",
          "streetName",
          "streetNumber",
          "country",
          "city",
          "state",
          "zip",
          "dateOfBirth",
          "birthPlace",
          "birthProvince",
          "birthNation",
          "sex",
          "ssnNumber",
          "vatNumber",
          "companyName",
        ];
        const dateFields = new Set([
          "dateOfBirth",
          "releaseDate",
          "documentExpiryDate",
          "releaseDate1",
          "expiryDate1",
        ]);
        const normalizeValue = (field: string, val: any): string => {
          if (val == null || val === "") return "";
          if (dateFields.has(field)) {
            const s = String(val);
            return s.includes("T") ? s.split("T")[0] : s;
          }
          return String(val);
        };
        const hasChanges = fieldsToCompare.some(
          (field) =>
            normalizeValue(field, updatedDetails[field]) !==
            normalizeValue(field, (details as any)[field])
        );
        if (hasChanges) {
          try {
            await BookingApi.archiveCustomer(details.customerId);
          } catch (error) {
            console.error("Failed to archive foreigner customer:", error);
          }
          try {
            const insertResponse = await BookingApi.insertNewCustomer(
              reservationDetails.reservationId,
              {
                ...updatedDetails,
                isCustPhysicalPerson: reservationDetails.isCustPhysicalPerson,
                isCustIndividualCompany:
                  reservationDetails.isCustIndividualCompany,
              }
            );
            const newCustomerId =
              insertResponse?.data?.customerId ||
              insertResponse?.data?.data?.customerId;
            if (newCustomerId) {
              updatedDetails = {
                ...updatedDetails,
                customerId: newCustomerId,
              };
            }
          } catch (error) {
            console.error(
              "Failed to insert new customer for foreigner:",
              error
            );
          }
        } else {
          try {
            await BookingApi.updateCustomerDetails(
              updatedDetails.customerId,
              updatedDetails
            );
          } catch (error) {
            console.error("Failed to update customer details:", error);
          }
        }
      } else if (!updatedDetails.customerId) {
        try {
          const insertResponse = await BookingApi.insertNewCustomer(
            reservationDetails.reservationId,
            {
              ...updatedDetails,
              isCustPhysicalPerson: reservationDetails.isCustPhysicalPerson,
              isCustIndividualCompany:
                reservationDetails.isCustIndividualCompany,
            }
          );
          const newCustomerId =
            insertResponse?.data?.customerId ||
            insertResponse?.data?.data?.customerId;
          if (newCustomerId) {
            updatedDetails = { ...updatedDetails, customerId: newCustomerId };
          }
        } catch (error) {
          console.error("Failed to insert new customer:", error);
        }
      } else {
        try {
          await BookingApi.updateCustomerDetails(
            updatedDetails.customerId,
            updatedDetails
          );
        } catch (error) {
          console.error("Failed to update customer details:", error);
        }
      }

      setIsLoadingCustomerData(false);
      onNext(updatedDetails);
    } else {
      if (
        currentSection === 4 &&
        details?.customerType !== "COMPANY" &&
        !isNotItalian &&
        appSettings?.restrictBlacklistCustomersFromBooking
      ) {
        const blacklistId = data.ssnNumber || data.vatNumber;
        if (blacklistId) {
          const blacklistResponse =
            await BookingApi.checkIfCustomerIsBlacklisted(String(blacklistId));
          if (blacklistResponse?.data?.status === "success") {
            showErrorModal(isBlacklistedText);
            return;
          }
        }
      }
      setCurrentSection(currentSection + 1);
    }
  };

  const handleBack = (): void => {
    if (details?.customerType === "COMPANY") {
      if (currentSection > 1) {
        setCurrentSection(currentSection - 1);
      } else {
        onBack();
      }
      return;
    }

    if (details?.customerType !== "COMPANY") {
      if (currentSection > 1) {
        setCurrentSection(currentSection - 1);
      } else {
        onBack();
      }
    }
  };

  const [birthNation, birthPlace] = useWatch({
    control,
    name: ["birthNation", "birthPlace"],
  }) as [string, string];

  const selectedCountrys = useWatch({ control, name: "country" });
  const selectedBirthNations = useWatch({ control, name: "birthNation" });
  const [
    watchedPhNum1,
    watchedEmail,
    watchedAddress,
    watchedStreetNumber,
    watchedState,
    watchedZip,
    watchedBirthProvince,
  ] = useWatch({
    control,
    name: [
      "phNum1",
      "email",
      "address",
      "streetNumber",
      "state",
      "zip",
      "birthProvince",
    ],
  });
  // console.log("details", details);
  const showDropDownItalyAddress =
    selectedCountrys?.toUpperCase() === "ITALIA" ||
    selectedCountrys?.toUpperCase() === "ITALY";

  const showDropDownItalyBirth =
    selectedBirthNations?.toUpperCase() === "ITALIA" ||
    selectedBirthNations?.toUpperCase() === "ITALY";

  const [selectedCountry, selectedCity] = useWatch({
    control,
    name: ["country", "city"],
  }) as [string, string];

  const watchedBirthNation = watch("birthNation");
  const isClearingFields = useRef(false);

  const [prevCountry, setPrevCountry] = useState(selectedCountrys);
  const [prevBirthNation, setPrevBirthNation] = useState(selectedBirthNations);
  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        if (selectedCountry) {
          const response = await BookingApi.getStates(selectedCountry, country);
          if (isMountedRef.current) {
            setCities(response.data || []);
          }
          const wasItaly =
            prevCountry &&
            (prevCountry.toUpperCase() === "ITALIA" ||
              prevCountry.toUpperCase() === "ITALY");
          const isItaly = showDropDownItalyAddress;

          if (prevCountry && wasItaly !== isItaly && isMountedRef.current) {
            isClearingFields.current = true;

            setValue("city", "", { shouldValidate: false });
            setValue("state", "", { shouldValidate: false });
            setValue("zip", "", { shouldValidate: false });

            clearErrors(["city", "state", "zip"]);

            setTimeout(() => {
              isClearingFields.current = false;
            }, 100);
          }
          if (isMountedRef.current) {
            setPrevCountry(selectedCountry);
          }
        } else if (isMountedRef.current) {
          setCities([]);
        }

        if (selectedCity && showDropDownItalyAddress) {
          const response = await BookingApi.getCity(selectedCity);
          if (isMountedRef.current) {
            setStates(response.data.state || "");
            setZipCode(response.data.zip || "");
          }
        } else if (isMountedRef.current) {
          setStates([]);
          setZipCode([]);
        }
      } catch (err) {
        console.error("Error fetching location data:", err);
      }
    };

    fetchData();
  }, [
    selectedCountry,
    selectedCity,
    country,
    showDropDownItalyAddress,
    setValue,
    prevCountry,
    clearErrors,
  ]);

  // Similarly for birth information
  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        if (birthNation) {
          const response = await BookingApi.getStates(birthNation, country);
          if (isMountedRef.current) {
            setBirthCities(response.data || []);
          }

          const wasItaly =
            prevBirthNation &&
            (prevBirthNation.toUpperCase() === "ITALIA" ||
              prevBirthNation.toUpperCase() === "ITALY");
          const isItaly = showDropDownItalyBirth;

          if (prevBirthNation && wasItaly !== isItaly && isMountedRef.current) {
            isClearingFields.current = true;
            setValue("birthPlace", "", { shouldValidate: false });
            setValue("birthProvince", "", { shouldValidate: false });
            clearErrors(["birthPlace", "birthProvince"]);
            setTimeout(() => {
              isClearingFields.current = false;
            }, 100);
          }
          if (isMountedRef.current) {
            setPrevBirthNation(birthNation);
          }
        } else if (isMountedRef.current) {
          setBirthCities([]);
        }

        if (birthPlace && showDropDownItalyBirth) {
          const response = await BookingApi.getCity(birthPlace);
          if (isMountedRef.current) {
            setBirthStates(response.data.state || "");
          }
        } else if (isMountedRef.current) {
          setBirthStates([]);
        }
      } catch (err) {
        console.error("Error fetching location data:", err);
      }
    };

    fetchData();
  }, [
    birthNation,
    birthPlace,
    country,
    showDropDownItalyBirth,
    setValue,
    prevBirthNation,
    clearErrors,
  ]);

  const getValidationRules = () => {
    if (isClearingFields.current) {
      return {
        city: { required: false },
        state: { required: false },
        zip: { required: false },
        birthPlace: { required: false },
        birthProvince: { required: false },
      };
    }

    const isItalyAddress =
      watchedCountry?.toUpperCase() === "ITALIA" ||
      watchedCountry?.toUpperCase() === "ITALY";

    const isItalyBirth =
      watchedBirthNation?.toUpperCase() === "ITALIA" ||
      watchedBirthNation?.toUpperCase() === "ITALY";

    return {
      city: {
        required: requiredConfig.city
          ? `${translate("City")} ${translate("is required")}`
          : false,
      },
      state: {
        required: requiredConfig.state
          ? `${translate("State")} ${translate("is required")}`
          : false,
      },
      zip: {
        required: requiredConfig.zipCode
          ? `${translate("Zip Code")} ${translate("is required")}`
          : false,
      },
      birthPlace: {
        required: requiredConfig.birthPlace
          ? `${translate("Birth Place")} ${translate("is required")}`
          : false,
      },
      birthProvince: {
        required: requiredConfig.birthProvince
          ? `${translate("Birth Province")} ${translate("is required")}`
          : false,
      },
    };
  };

  const handleClose = (): void => {
    setOpenModal(false);
  };

  const getDateRules = (fieldName: string, label: string) => ({
    ...getRules(fieldName, label),
    validate: (value: any) => {
      if (!value) return true;
      const d = value instanceof Date ? value : new Date(value);
      if (isNaN(d.getTime())) return translate("Invalid date");
      if (d.getFullYear() > 3000) return translate("Invalid date");
      return true;
    },
  });

  const renderSection1 = () => (
    <>
      {details?.customerType === "COMPANY" ? (
        <>
          <Controller
            name="companyName"
            control={control}
            defaultValue={
              enableUppercaseInputField
                ? (details?.companyName || "").toUpperCase()
                : details?.companyName || ""
            }
            rules={{
              required: `${translate("Company Name")} ${translate(
                "is required"
              )}`,
            }}
            render={({ field }) => (
              <>
                <Typography variant="body2" className="webCheckInText">
                  {translate("Company Name")}
                </Typography>
                <TextField
                  {...field}
                  fullWidth
                  error={!!errors.companyName}
                  helperText={errors.companyName?.message}
                  className={`webCheckInTextField ${
                    details?.companyName || isInvoiceAlreadyCreated
                      ? "readOnlyField"
                      : ""
                  }`}
                  InputLabelProps={{ shrink: false }}
                  inputProps={{
                    readOnly: !!details?.companyName || isInvoiceAlreadyCreated,
                    style: enableUppercaseInputField
                      ? { textTransform: "uppercase" }
                      : {},
                  }}
                  onChange={(e) =>
                    field.onChange(
                      enableUppercaseInputField
                        ? e.target.value.toUpperCase()
                        : e.target.value
                    )
                  }
                />
              </>
            )}
          />

          <Controller
            name="vatNumber"
            control={control}
            defaultValue={details?.vatNumber || ""}
            rules={{
              required: `${translate("Vat Number")} ${translate(
                "is required"
              )}`,
            }}
            render={({ field }) => (
              <>
                <Typography variant="body2" className="webCheckInText">
                  {translate("VAT number")}
                </Typography>
                <TextField
                  {...field}
                  fullWidth
                  error={!!errors.vatNumber}
                  helperText={errors.vatNumber?.message}
                  InputLabelProps={{ shrink: false }}
                  className={`webCheckInTextField ${
                    isInvoiceAlreadyCreated ? "readOnlyField" : ""
                  }`}
                  inputProps={{
                    readOnly: isInvoiceAlreadyCreated,
                    style: enableUppercaseInputField
                      ? { textTransform: "uppercase" }
                      : {},
                  }}
                  onChange={(e) => {
                    field.onChange(
                      enableUppercaseInputField
                        ? e.target.value.toUpperCase()
                        : e.target.value
                    );
                    trigger("vatNumber");
                  }}
                />
              </>
            )}
          />
        </>
      ) : (
        <>
          <Controller
            name="firstName"
            control={control}
            defaultValue={
              enableUppercaseInputField
                ? (details?.firstName || "").toUpperCase()
                : details?.firstName || ""
            }
            rules={{
              required: `${translate("First Name")} ${translate(
                "is required"
              )}`,
            }}
            render={({ field }) => (
              <>
                <Typography variant="body2" className="webCheckInText">
                  {translate("First Name")}
                </Typography>
                <TextField
                  {...field}
                  fullWidth
                  error={!!errors.firstName}
                  helperText={errors.firstName?.message}
                  className={`webCheckInTextField ${
                    details?.firstName || isInvoiceAlreadyCreated
                      ? "readOnlyField"
                      : ""
                  }`}
                  InputLabelProps={{ shrink: false }}
                  inputProps={{
                    readOnly: !!details?.firstName || isInvoiceAlreadyCreated,
                    style: enableUppercaseInputField
                      ? { textTransform: "uppercase" }
                      : {},
                  }}
                  onChange={(e) =>
                    field.onChange(
                      enableUppercaseInputField
                        ? e.target.value.toUpperCase()
                        : e.target.value
                    )
                  }
                />
              </>
            )}
          />

          <Controller
            name="lastName"
            control={control}
            defaultValue={
              enableUppercaseInputField
                ? (details?.lastName || "").toUpperCase()
                : details?.lastName || ""
            }
            rules={{
              required: `${translate("Last Name")} ${translate("is required")}`,
            }}
            render={({ field }) => (
              <>
                <Typography variant="body2" className="webCheckInText">
                  {translate("Last Name")}
                </Typography>
                <TextField
                  {...field}
                  fullWidth
                  error={!!errors.lastName}
                  helperText={errors.lastName?.message}
                  className={`webCheckInTextField ${
                    details?.lastName || isInvoiceAlreadyCreated
                      ? "readOnlyField"
                      : ""
                  }`}
                  InputLabelProps={{ shrink: false }}
                  inputProps={{
                    readOnly: !!details?.lastName || isInvoiceAlreadyCreated,
                    style: enableUppercaseInputField
                      ? { textTransform: "uppercase" }
                      : {},
                  }}
                  onChange={(e) =>
                    field.onChange(
                      enableUppercaseInputField
                        ? e.target.value.toUpperCase()
                        : e.target.value
                    )
                  }
                />
              </>
            )}
          />
        </>
      )}

      <Divider className="webCheckInDivider" />

      <Controller
        name="phNum1"
        control={control}
        rules={{
          required: `${translate("Phone Number")} ${translate("is required")}`,
          validate: (value) => {
            const digits = (value || "").replace(/\D/g, "");
            return (
              digits.length >= 5 ||
              `${translate("Phone Number")} ${translate("is required")}`
            );
          },
        }}
        // rules={getRules("phoneNumber", "Phone Number")}
        render={({ field }) => (
          <>
            <Typography variant="body2" className="webCheckInText">
              {translate("Phone Number")}
            </Typography>
            {/*<RHFPhoneInput {...field} country="it" />*/}
            <RHFPhoneInput
              {...field}
              country="it"
              onChange={(val: string) => {
                field.onChange(val);
                trigger("phNum1");
              }}
            />
            {errors.phNum1 && (
              <Typography variant="caption" color="error">
                {errors.phNum1.message}
              </Typography>
            )}
          </>
        )}
      />

      <Controller
        name="email"
        defaultValue=""
        control={control}
        rules={{
          ...getRules("email", "Email"),
          pattern: {
            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
            message: translate("Email is invalid"),
          },
        }}
        render={({ field }) => (
          <>
            <Typography variant="body2" className="webCheckInText">
              {translate("Email")}
            </Typography>
            <TextField
              {...field}
              type="email"
              fullWidth
              error={!!errors.email}
              helperText={errors.email?.message}
              className="webCheckInTextField"
              InputLabelProps={{ shrink: false }}
              inputProps={
                enableUppercaseInputField
                  ? { style: { textTransform: "uppercase" } }
                  : {}
              }
              onChange={(e) => {
                field.onChange(
                  enableUppercaseInputField
                    ? e.target.value.toUpperCase()
                    : e.target.value
                );
                trigger("email");
              }}
            />
          </>
        )}
      />
    </>
  );

  // Render section 2: Address Information
  const renderSection2 = () => {
    const uniqueCountries = Array.from(new Set(countries || []));

    return (
      <>
        <Grid container spacing={2}>
          <Grid item xs={8}>
            <Controller
              name="address"
              defaultValue=""
              control={control}
              rules={getRules("streetName", "Address")}
              render={({ field }) => (
                <>
                  <Typography variant="body2" className="webCheckInText">
                    {translate("Address")}
                  </Typography>
                  <TextField
                    {...field}
                    fullWidth
                    error={!!errors.address}
                    helperText={errors.address?.message}
                    className="webCheckInTextField"
                    InputLabelProps={{ shrink: false }}
                    inputProps={
                      enableUppercaseInputField
                        ? { style: { textTransform: "uppercase" } }
                        : {}
                    }
                    onChange={(e) =>
                      field.onChange(
                        enableUppercaseInputField
                          ? e.target.value.toUpperCase()
                          : e.target.value
                      )
                    }
                  />
                </>
              )}
            />
          </Grid>
          <Grid item xs={4}>
            <Controller
              name="streetNumber"
              defaultValue=""
              control={control}
              rules={getRules("streetNumber", "N")}
              render={({ field }) => (
                <>
                  <Typography variant="body2" className="webCheckInText">
                    {translate("N")}
                  </Typography>
                  <TextField
                    {...field}
                    fullWidth
                    error={!!errors.streetNumber}
                    helperText={errors.streetNumber?.message}
                    className="webCheckInTextField"
                    InputLabelProps={{ shrink: false }}
                    FormHelperTextProps={{ sx: { whiteSpace: "nowrap" } }}
                    inputProps={
                      enableUppercaseInputField
                        ? { style: { textTransform: "uppercase" } }
                        : {}
                    }
                    onChange={(e) =>
                      field.onChange(
                        enableUppercaseInputField
                          ? e.target.value.toUpperCase()
                          : e.target.value
                      )
                    }
                  />
                </>
              )}
            />
          </Grid>
        </Grid>

        <Controller
          name="country"
          control={control}
          defaultValue=""
          rules={getRules("country", "Country")}
          render={({ field }) => (
            <>
              <Typography variant="body2" className="webCheckInText">
                {translate("Country")}
              </Typography>
              <div
                className={`webCheckInTextField ${
                  isInvoiceAlreadyCreated ? "readOnlyField" : ""
                }`}
              >
                <AppCountrySelect
                  fieldKey="country"
                  label=""
                  value={field.value || ""}
                  options={uniqueCountries}
                  required={!!requiredConfig.country}
                  readOnly={isInvoiceAlreadyCreated}
                  translate={translate}
                  formStates={{
                    setValue: (key: string, val: any, opts?: any) => {
                      setValue(key, val, { shouldValidate: true, ...opts });
                      if (key === "country") field.onChange(val);
                    },
                  }}
                  setUserDetails={() => {}}
                  onChange={(val: string) => {
                    field.onChange(val);
                  }}
                  cityFieldName="city"
                  stateFieldName="state"
                  zipFieldName="zip"
                  enableUppercaseInputField={enableUppercaseInputField}
                />
              </div>
              <FormHelperText error>{errors.country?.message}</FormHelperText>
            </>
          )}
        />

        <Controller
          name="city"
          defaultValue=""
          control={control}
          rules={getRules("city", "City")}
          // rules={getValidationRules().city}
          render={({ field }) => (
            <>
              <Typography variant="body2" className="webCheckInText">
                {translate("City")}
              </Typography>
              {showDropDownItalyAddress ? (
                <Autocomplete
                  {...field}
                  options={cities}
                  value={field.value || ""}
                  disableClearable
                  filterOptions={(options, state) => {
                    const effectiveInput =
                      state.inputValue || field.value || "";
                    return prefixFilterOptions(options, {
                      ...state,
                      inputValue: effectiveInput,
                    });
                  }}
                  openOnFocus
                  getOptionLabel={(option) =>
                    typeof option === "string" ? option : String(option)
                  }
                  isOptionEqualToValue={(option, value) => option === value}
                  onChange={async (_, value) => {
                    field.onChange(
                      enableUppercaseInputField
                        ? (value || "").toUpperCase()
                        : value
                    );
                  }}
                  onInputChange={(_, value) => {
                    field.onChange(
                      enableUppercaseInputField
                        ? (value || "").toUpperCase()
                        : value
                    );
                  }}
                  disabled={isInvoiceAlreadyCreated}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      error={!!errors.city}
                      helperText={errors.city?.message}
                      // className="webCheckInTextField"
                      className={`webCheckInTextField ${
                        isInvoiceAlreadyCreated ? "readOnlyField" : ""
                      }`}
                      inputProps={{
                        ...params.inputProps,
                        style: enableUppercaseInputField
                          ? { textTransform: "uppercase" }
                          : {},
                      }}
                    />
                  )}
                />
              ) : (
                <TextField
                  {...field}
                  fullWidth
                  error={!!errors.city}
                  helperText={errors.city?.message}
                  className="webCheckInTextField"
                  inputProps={
                    enableUppercaseInputField
                      ? { style: { textTransform: "uppercase" } }
                      : {}
                  }
                  onChange={(e) =>
                    field.onChange(
                      enableUppercaseInputField
                        ? e.target.value.toUpperCase()
                        : e.target.value
                    )
                  }
                />
              )}
            </>
          )}
        />

        <Grid container spacing={2}>
          <Grid item xs={isSmallScreen ? 5.5 : 7}>
            <Controller
              name="state"
              defaultValue=""
              control={control}
              rules={getRules("state", "State")}
              // rules={getValidationRules().state}
              render={({ field }) => (
                <>
                  <Typography variant="body2" className="webCheckInText">
                    {translate("State")}
                  </Typography>
                  {showDropDownItalyAddress ? (
                    <Autocomplete
                      {...field}
                      options={states}
                      value={field.value || ""}
                      disableClearable
                      getOptionLabel={(option) =>
                        typeof option === "string" ? option : String(option)
                      }
                      isOptionEqualToValue={(option, value) => option === value}
                      onChange={(_, value) =>
                        field.onChange(
                          enableUppercaseInputField
                            ? (value || "").toUpperCase()
                            : value
                        )
                      }
                      onInputChange={(_, value) =>
                        field.onChange(
                          enableUppercaseInputField
                            ? (value || "").toUpperCase()
                            : value
                        )
                      }
                      disabled={isInvoiceAlreadyCreated}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          error={!!errors.state}
                          helperText={errors.state?.message}
                          // className="webCheckInTextField"
                          className={`webCheckInTextField ${
                            isInvoiceAlreadyCreated ? "readOnlyField" : ""
                          }`}
                          inputProps={{
                            ...params.inputProps,
                            style: enableUppercaseInputField
                              ? { textTransform: "uppercase" }
                              : {},
                          }}
                        />
                      )}
                    />
                  ) : (
                    <TextField
                      {...field}
                      fullWidth
                      error={!!errors.state}
                      helperText={errors.state?.message}
                      className="webCheckInTextField"
                      inputProps={
                        enableUppercaseInputField
                          ? { style: { textTransform: "uppercase" } }
                          : {}
                      }
                      onChange={(e) =>
                        field.onChange(
                          enableUppercaseInputField
                            ? e.target.value.toUpperCase()
                            : e.target.value
                        )
                      }
                    />
                  )}
                </>
              )}
            />
          </Grid>

          <Grid item xs={isSmallScreen ? 6.3 : 5}>
            <Controller
              name="zip"
              defaultValue=""
              control={control}
              rules={getRules("zipCode", "Zip Code")}
              // rules={getValidationRules().zip}
              render={({ field }) => (
                <>
                  <Typography variant="body2" className="webCheckInText">
                    {translate("Zip Code")}
                  </Typography>
                  {showDropDownItalyAddress ? (
                    <Autocomplete
                      {...field}
                      options={zipCode}
                      value={field.value || ""}
                      disableClearable
                      getOptionLabel={(option) =>
                        typeof option === "string" ? option : String(option)
                      }
                      isOptionEqualToValue={(option, value) => option === value}
                      onChange={(_, value) =>
                        field.onChange(
                          enableUppercaseInputField
                            ? (value || "").toUpperCase()
                            : value
                        )
                      }
                      onInputChange={(_, value) =>
                        field.onChange(
                          enableUppercaseInputField
                            ? (value || "").toUpperCase()
                            : value
                        )
                      }
                      disabled={isInvoiceAlreadyCreated}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          error={!!errors.zip}
                          helperText={errors.zip?.message}
                          // className="webCheckInTextField"
                          className={`webCheckInTextField ${
                            isInvoiceAlreadyCreated ? "readOnlyField" : ""
                          }`}
                          FormHelperTextProps={{ sx: { whiteSpace: "nowrap" } }}
                          inputProps={{
                            ...params.inputProps,
                            style: enableUppercaseInputField
                              ? { textTransform: "uppercase" }
                              : {},
                          }}
                        />
                      )}
                    />
                  ) : (
                    <TextField
                      {...field}
                      fullWidth
                      error={!!errors.zip}
                      helperText={errors.zip?.message}
                      className="webCheckInTextField"
                      FormHelperTextProps={{ sx: { whiteSpace: "nowrap" } }}
                      inputProps={
                        enableUppercaseInputField
                          ? { style: { textTransform: "uppercase" } }
                          : {}
                      }
                      onChange={(e) =>
                        field.onChange(
                          enableUppercaseInputField
                            ? e.target.value.toUpperCase()
                            : e.target.value
                        )
                      }
                    />
                  )}
                </>
              )}
            />
          </Grid>
        </Grid>
      </>
    );
  };

  // Render section 3: Birth Information
  const renderSection3 = () => {
    const uniqueCountries = Array.from(new Set(countries));
    return (
      <>
        <LocalizationProvider
          dateAdapter={AdapterDateFns}
          adapterLocale={dateLocale}
          localeText={muiLocaleText}
        >
          <Controller
            name="dateOfBirth"
            control={control}
            defaultValue=""
            rules={getDateRules("birthDate", "Date of birth")}
            render={({ field }) => (
              <>
                <Typography variant="body2" className="webCheckInText">
                  {translate("Date of birth")}
                </Typography>
                <DesktopDatePicker
                  value={field.value}
                  onChange={(newValue) => field.onChange(newValue)}
                  inputFormat="dd/MM/yyyy"
                  disabled={isInvoiceAlreadyCreated}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      className={`webCheckInTextField ${
                        isInvoiceAlreadyCreated ? "readOnlyField" : ""
                      }`}
                      error={!!errors.dateOfBirth}
                      helperText={errors.dateOfBirth?.message}
                      inputProps={{
                        ...params.inputProps,
                        placeholder: "",
                        readOnly: isInvoiceAlreadyCreated,
                      }}
                    />
                  )}
                />
              </>
            )}
          />
        </LocalizationProvider>

        <Controller
          name="birthNation"
          control={control}
          defaultValue=""
          rules={getRules("birthNation", "Country of birth")}
          render={({ field }) => (
            <>
              <Typography variant="body2" className="webCheckInText">
                {translate("Country of birth")}
              </Typography>
              <div
                className={`webCheckInTextField ${
                  isInvoiceAlreadyCreated ? "readOnlyField" : ""
                }`}
              >
                <AppCountrySelect
                  fieldKey="birthNation"
                  label=""
                  value={field.value || ""}
                  options={uniqueCountries}
                  required={!!requiredConfig.birthNation}
                  readOnly={isInvoiceAlreadyCreated}
                  translate={translate}
                  formStates={{
                    setValue: (key: string, val: any, opts?: any) => {
                      setValue(key, val, { shouldValidate: true, ...opts });
                      if (key === "birthNation") field.onChange(val);
                    },
                  }}
                  setUserDetails={() => {}}
                  onChange={(val: string) => {
                    field.onChange(val);
                  }}
                  cityFieldName="birthPlace"
                  stateFieldName="birthProvince"
                  enableUppercaseInputField={enableUppercaseInputField}
                />
              </div>
              <FormHelperText error>
                {errors.birthNation?.message}
              </FormHelperText>
            </>
          )}
        />

        <Controller
          name="birthPlace"
          defaultValue=""
          control={control}
          rules={getRules("birthPlace", "Birth Place")}
          // rules={getValidationRules().birthPlace}
          render={({ field }) => (
            <>
              <Typography variant="body2" className="webCheckInText">
                {translate("Place of birth")}
              </Typography>
              {showDropDownItalyBirth ? (
                <Autocomplete
                  {...field}
                  options={birthCities}
                  value={field.value || ""}
                  disableClearable
                  filterOptions={prefixFilterOptions}
                  getOptionLabel={(option) =>
                    typeof option === "string" ? option : String(option)
                  }
                  onInputChange={(_, value) =>
                    field.onChange(
                      enableUppercaseInputField
                        ? (value || "").toUpperCase()
                        : value
                    )
                  }
                  isOptionEqualToValue={(option, value) => option === value}
                  onChange={async (_, value) => {
                    field.onChange(
                      enableUppercaseInputField
                        ? (value || "").toUpperCase()
                        : value
                    );
                  }}
                  disabled={isInvoiceAlreadyCreated}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      error={!!errors.birthPlace}
                      helperText={errors.birthPlace?.message}
                      className={`webCheckInTextField ${
                        isInvoiceAlreadyCreated ? "readOnlyField" : ""
                      }`}
                      inputProps={{
                        ...params.inputProps,
                        style: enableUppercaseInputField
                          ? { textTransform: "uppercase" }
                          : {},
                      }}
                    />
                  )}
                />
              ) : (
                <TextField
                  {...field}
                  fullWidth
                  error={!!errors.birthPlace}
                  helperText={errors.birthPlace?.message}
                  className="webCheckInTextField"
                  inputProps={
                    enableUppercaseInputField
                      ? { style: { textTransform: "uppercase" } }
                      : {}
                  }
                  onChange={(e) =>
                    field.onChange(
                      enableUppercaseInputField
                        ? e.target.value.toUpperCase()
                        : e.target.value
                    )
                  }
                />
              )}
            </>
          )}
        />

        <Controller
          name="birthProvince"
          defaultValue=""
          control={control}
          rules={getRules("birthProvince", "Birth Province")}
          // rules={getValidationRules().birthProvince}
          render={({ field }) => (
            <>
              <Typography variant="body2" className="webCheckInText">
                {translate("State of birth")}
              </Typography>
              {showDropDownItalyBirth ? (
                <Autocomplete
                  {...field}
                  options={birthStates}
                  value={field.value || ""}
                  disableClearable
                  getOptionLabel={(option) =>
                    typeof option === "string" ? option : String(option)
                  }
                  isOptionEqualToValue={(option, value) => option === value}
                  onInputChange={(_, value) =>
                    field.onChange(
                      enableUppercaseInputField
                        ? (value || "").toUpperCase()
                        : value
                    )
                  }
                  onChange={(_, value) =>
                    field.onChange(
                      enableUppercaseInputField
                        ? (value || "").toUpperCase()
                        : value
                    )
                  }
                  disabled={isInvoiceAlreadyCreated}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      error={!!errors.birthProvince}
                      helperText={errors.birthProvince?.message}
                      className={`webCheckInTextField ${
                        isInvoiceAlreadyCreated ? "readOnlyField" : ""
                      }`}
                      inputProps={{
                        ...params.inputProps,
                        style: enableUppercaseInputField
                          ? { textTransform: "uppercase" }
                          : {},
                      }}
                    />
                  )}
                />
              ) : (
                <TextField
                  {...field}
                  fullWidth
                  error={!!errors.birthProvince}
                  helperText={errors.birthProvince?.message}
                  className="webCheckInTextField"
                  inputProps={
                    enableUppercaseInputField
                      ? { style: { textTransform: "uppercase" } }
                      : {}
                  }
                  onChange={(e) =>
                    field.onChange(
                      enableUppercaseInputField
                        ? e.target.value.toUpperCase()
                        : e.target.value
                    )
                  }
                />
              )}
            </>
          )}
        />
      </>
    );
  };

  // Render section 4: Gender Information
  const renderSection4 = () => (
    <>
      <Controller
        name="sex"
        defaultValue=""
        control={control}
        rules={getRules("sex", "Gender")}
        render={({ field }) => (
          <>
            <Typography variant="body2" className="webCheckInText">
              {translate("Gender")}
            </Typography>

            <RadioGroup
              row
              {...field}
              onChange={(e) => field.onChange(e.target.value)}
              value={field.value}
              sx={webCheckInRadioGroup}
            >
              <FormControlLabel
                value="FEMALE"
                control={
                  <Radio
                    sx={{
                      "&.Mui-checked": {
                        color: isInvoiceAlreadyCreated
                          ? "readOnlyField"
                          : webCheckinSecondaryColor || "#ff238c",
                      },
                    }}
                  />
                }
                label={translate("Female")}
                disabled={isInvoiceAlreadyCreated}
              />
              <FormControlLabel
                value="MALE"
                control={
                  <Radio
                    sx={{
                      "&.Mui-checked": {
                        color: isInvoiceAlreadyCreated
                          ? "readOnlyField"
                          : webCheckinSecondaryColor || "#ff238c",
                      },
                    }}
                  />
                }
                label={translate("Male")}
                disabled={isInvoiceAlreadyCreated}
              />
            </RadioGroup>

            {errors.sex && (
              <FormHelperText error>{errors.sex.message}</FormHelperText>
            )}
          </>
        )}
      />
      <Divider className="webCheckInDivider" />

      <Controller
        name="ssnNumber"
        defaultValue=""
        control={control}
        rules={getRules("ssnNumber", "SSN1")}
        render={({ field }) => {
          return (
            <>
              <Typography variant="body2" className="webCheckInText">
                {translate("Tax Identification Number (TIN)")}
              </Typography>
              <TextField
                {...field}
                fullWidth
                error={!!errors.ssnNumber}
                helperText={errors.ssnNumber?.message}
                className={`webCheckInTextField ${
                  isInvoiceAlreadyCreated ? "readOnlyField" : ""
                }`}
                InputLabelProps={{ shrink: false }}
                disabled={isNotItalian}
                inputProps={{
                  readOnly: isNotItalian || isInvoiceAlreadyCreated,
                  style: enableUppercaseInputField
                    ? { textTransform: "uppercase" }
                    : {},
                }}
                onChange={(e) =>
                  field.onChange(
                    enableUppercaseInputField
                      ? e.target.value.toUpperCase()
                      : e.target.value
                  )
                }
              />
            </>
          );
        }}
      />
    </>
  );

  // Render section 5: Select Your Profile
  const renderSection5 = () => {
    const isNewSelected = selectedProfile === "new";
    const isExistingSelected = selectedProfile === "existing";

    const cardStyle = (isSelected: boolean) => ({
      border: isSelected
        ? `2px solid ${webCheckinHeaderBgColor || "#2cb133"}`
        : "2px solid #e8e8e8",
      borderRadius: "16px",
      padding: isSmallScreen ? "12px" : "20px",
      cursor: "pointer",
      height: "100%",
      display: "flex",
      flexDirection: "column" as const,
      transition: "all 0.25s ease",
      backgroundColor: "#fff",
      boxShadow: isSelected
        ? "0 4px 20px rgba(0,188,212,0.18)"
        : "0 2px 8px rgba(0,0,0,0.06)",
    });

    const FieldRow = ({
      label,
      value,
      greyed,
    }: {
      label: string;
      value: string | React.ReactNode;
      greyed: boolean;
    }) => (
      <Box sx={{ mb: 1 }}>
        <Typography
          variant="caption"
          sx={{
            color: greyed ? "#ccc" : "#888",
            fontWeight: 600,
            fontSize: "10px",
            letterSpacing: 0.5,
            display: "block",
            mb: 0.2,
          }}
        >
          {label}
        </Typography>
        {typeof value === "string" ? (
          <Typography
            variant="body2"
            sx={{
              color: greyed ? "#bbb" : "#1a1a1a",
              fontWeight: greyed ? 400 : 600,
              fontSize: "15px",
            }}
          >
            {value}
          </Typography>
        ) : (
          value
        )}
        <Divider sx={{ mt: 1, borderColor: greyed ? "#f0f0f0" : "#ebebeb" }} />
      </Box>
    );

    return (
      <Box sx={{ mx: -3, mt: -2 }}>
        {/* Cards wrapper */}
        <Box sx={{ backgroundColor: "#fff", p: { xs: 1, sm: 2 } }}>
          <Grid container spacing={{ xs: 1, sm: 2 }}>
            {/* NEW DETAILS card */}
            <Grid item xs={12} sm={6}>
              <Box
                sx={cardStyle(isNewSelected)}
                onClick={() => setSelectedProfile("new")}
              >
                {/* Card header */}
                <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
                  <AccountCircleIcon
                    sx={{
                      mr: 0.8,
                      color: isNewSelected ? "#1a1a1a" : "#ccc",
                      fontSize: 22,
                    }}
                  />
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 800,
                      fontSize: "18px",
                      letterSpacing: 0.5,
                      color: isNewSelected ? "#1a1a1a" : "#bbb",
                    }}
                  >
                    {translate("NEW DETAILS")}
                  </Typography>
                </Box>
                <Divider sx={{ mb: 1.5 }} />

                {details?.customerType === "COMPANY" ? (
                  <>
                    <FieldRow
                      label={translate("Company Name")}
                      value={newProfileSnapshot?.companyName || ""}
                      greyed={!isNewSelected}
                    />
                    <FieldRow
                      label={translate("VAT Number")}
                      value={newProfileSnapshot?.vatNumber || ""}
                      greyed={!isNewSelected}
                    />
                  </>
                ) : (
                  <FieldRow
                    label={translate("Customer Name")}
                    value={`${newProfileSnapshot?.firstName || ""} ${
                      newProfileSnapshot?.lastName || ""
                    }`.trim()}
                    greyed={!isNewSelected}
                  />
                )}
                <FieldRow
                  label={translate("Phone Number")}
                  value={
                    newProfileSnapshot?.phNum1 ||
                    newProfileSnapshot?.mobileNumber ||
                    ""
                  }
                  greyed={!isNewSelected}
                />
                <FieldRow
                  label={translate("Mail")}
                  value={newProfileSnapshot?.email || ""}
                  greyed={!isNewSelected}
                />
                <FieldRow
                  label={translate("Address")}
                  greyed={!isNewSelected}
                  value={
                    <Typography
                      variant="body2"
                      sx={{
                        color: isNewSelected ? "#1a1a1a" : "#bbb",
                        fontWeight: 600,
                        fontSize: "13px",
                      }}
                    >
                      {[
                        newProfileSnapshot?.address,
                        newProfileSnapshot?.streetNumber,
                        newProfileSnapshot?.zip,
                        newProfileSnapshot?.city,
                        newProfileSnapshot?.state,
                        newProfileSnapshot?.country,
                      ]
                        .filter(Boolean)
                        .join(", ")}
                    </Typography>
                  }
                />
                {details?.customerType !== "COMPANY" && (
                  <>
                    <FieldRow
                      label={translate("Date of Birth")}
                      value={
                        newProfileSnapshot?.dateOfBirth
                          ? typeof newProfileSnapshot.dateOfBirth === "string"
                            ? (newProfileSnapshot.dateOfBirth as string).split(
                                "T"
                              )[0]
                            : new Date(newProfileSnapshot.dateOfBirth)
                                .toISOString()
                                .split("T")[0]
                          : ""
                      }
                      greyed={!isNewSelected}
                    />
                    <FieldRow
                      label={translate("Place of Birth")}
                      greyed={!isNewSelected}
                      value={
                        <Typography
                          variant="body2"
                          sx={{
                            color: isNewSelected ? "#1a1a1a" : "#bbb",
                            fontWeight: 600,
                            fontSize: "13px",
                          }}
                        >
                          {[
                            newProfileSnapshot?.birthPlace,
                            newProfileSnapshot?.birthProvince,
                            newProfileSnapshot?.birthNation,
                          ]
                            .filter(Boolean)
                            .join(", ")}
                        </Typography>
                      }
                    />
                  </>
                )}

                {/* Radio footer */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: isSmallScreen ? "center" : "flex-end",
                    mt: "auto",
                    pt: 1.5,
                    gap: 0.5,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      textAlign: isSmallScreen ? "center" : "right",
                      color: "#555",
                      fontSize: "10px",
                      lineHeight: 1.3,
                    }}
                  >
                    {translate("Select this profile")}
                  </Typography>
                  <Radio
                    checked={isNewSelected}
                    onChange={() => setSelectedProfile("new")}
                    onClick={(e) => e.stopPropagation()}
                    size="small"
                    sx={{
                      p: 0.5,
                      color: webCheckinHeaderBgColor || "#2cb133",
                      "&.Mui-checked": {
                        color: webCheckinHeaderBgColor || "#2cb133",
                      },
                      flexShrink: 0,
                    }}
                  />
                </Box>
              </Box>
            </Grid>

            {/* EXISTING DETAILS card */}
            <Grid item xs={12} sm={6}>
              <Box
                sx={cardStyle(isExistingSelected)}
                onClick={() => setSelectedProfile("existing")}
              >
                {/* Card header */}
                <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
                  <AccountCircleIcon
                    sx={{
                      mr: 0.8,
                      color: isExistingSelected ? "#1a1a1a" : "#ccc",
                      fontSize: 22,
                    }}
                  />
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 800,
                      fontSize: "18px",
                      letterSpacing: 0.5,
                      color: isExistingSelected ? "#1a1a1a" : "#bbb",
                    }}
                  >
                    {translate("EXISTING DETAILS")}
                  </Typography>
                </Box>
                <Divider sx={{ mb: 1.5, borderColor: "#f0f0f0" }} />

                {details?.customerType === "COMPANY" ? (
                  <>
                    <FieldRow
                      label={translate("Company Name")}
                      value={
                        existingCustomerData?.customers?.companyName ||
                        existingCustomerData?.customers?.ragioneSociale ||
                        ""
                      }
                      greyed={!isExistingSelected}
                    />
                    <FieldRow
                      label={translate("VAT Number")}
                      value={
                        existingCustomerData?.customers?.vatNumber ||
                        existingCustomerData?.customers?.vat ||
                        ""
                      }
                      greyed={!isExistingSelected}
                    />
                  </>
                ) : (
                  <FieldRow
                    label={translate("Customer Name")}
                    value={`${
                      existingCustomerData?.customers?.firstName || ""
                    } ${
                      existingCustomerData?.customers?.lastName || ""
                    }`.trim()}
                    greyed={!isExistingSelected}
                  />
                )}
                <FieldRow
                  label={translate("Phone Number")}
                  value={
                    existingCustomerData?.customers?.telephone ||
                    existingCustomerData?.customers?.phoneNumber ||
                    existingCustomerData?.customers?.mobile ||
                    ""
                  }
                  greyed={!isExistingSelected}
                />
                <FieldRow
                  label={translate("Mail")}
                  value={
                    existingCustomerData?.customers?.email ||
                    existingCustomerData?.customers?.email_invoice ||
                    ""
                  }
                  greyed={!isExistingSelected}
                />
                <FieldRow
                  label={translate("Address")}
                  greyed={!isExistingSelected}
                  value={
                    <Typography
                      variant="body2"
                      sx={{
                        color: isExistingSelected ? "#1a1a1a" : "#bbb",
                        fontSize: "13px",
                        fontWeight: 600,
                      }}
                    >
                      {[
                        existingCustomerData?.customers?.street,
                        existingCustomerData?.customers?.addressNumber ||
                          existingCustomerData?.customers?.n,
                        existingCustomerData?.customers?.postalCode,
                        existingCustomerData?.customers?.city,
                        existingCustomerData?.customers?.province ||
                          existingCustomerData?.customers?.state,
                        existingCustomerData?.customers?.country,
                      ]
                        .filter(Boolean)
                        .join(", ")}
                    </Typography>
                  }
                />
                {details?.customerType !== "COMPANY" && (
                  <>
                    <FieldRow
                      label={translate("Date of Birth")}
                      value={
                        existingCustomerData?.customers?.dob
                          ? existingCustomerData.customers.dob.split("T")[0]
                          : ""
                      }
                      greyed={!isExistingSelected}
                    />
                    <FieldRow
                      label={translate("Place of Birth")}
                      greyed={!isExistingSelected}
                      value={
                        <Typography
                          variant="body2"
                          sx={{
                            color: isExistingSelected ? "#1a1a1a" : "#bbb",
                            fontSize: "13px",
                            fontWeight: 600,
                          }}
                        >
                          {[
                            existingCustomerData?.customers?.birthplace,
                            existingCustomerData?.customers?.birthProv,
                            existingCustomerData?.customers?.birthNation,
                          ]
                            .filter(Boolean)
                            .join(", ")}
                        </Typography>
                      }
                    />
                  </>
                )}

                {/* Radio footer */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    mt: "auto",
                    pt: 1.5,
                    gap: 0.5,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ color: "#aaa", fontSize: "10px" }}
                  >
                    {translate("Keep this profile")}
                  </Typography>
                  <Radio
                    checked={isExistingSelected}
                    onChange={() => setSelectedProfile("existing")}
                    onClick={(e) => e.stopPropagation()}
                    size="small"
                    sx={{
                      p: 0.5,
                      color: isExistingSelected
                        ? webCheckinHeaderBgColor || "#2cb133"
                        : "#ccc",
                      "&.Mui-checked": {
                        color: webCheckinHeaderBgColor || "#2cb133",
                      },
                      flexShrink: 0,
                    }}
                  />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>
    );
  };

  return (
    <>
      <WebCheckInLayout
        title={
          currentSection === 5
            ? translate("SELECT YOUR PROFILE")
            : translate("BILLING DETAILS")
        }
        progress={nestedProgress}
        onBack={handleBack}
        wide={currentSection === 5}
        isLoading={isLoadingCustomerData && !isNotItalian}
      >
        <form onSubmit={handleSubmit(handleContinue)}>
          {currentSection === 1 && renderSection1()}
          {currentSection === 2 && renderSection2()}

          {details?.customerType !== "COMPANY" &&
            currentSection === 3 &&
            renderSection3()}
          {details?.customerType !== "COMPANY" &&
            currentSection === 4 &&
            renderSection4()}
          {currentSection === 5 && hasExistingCustomer && renderSection5()}

          <Box textAlign="center">
            <Button
              type="submit"
              variant="contained"
              className="webCheckInButton"
              // disabled={isLoadingCustomerData && !isNotItalian}
              style={{
                borderRadius: `${appSettings?.buttonBorderRadius || 55}px`,
              }}
              sx={
                isShareboxEnabled
                  ? {
                      width: "100% !important",
                      maxWidth: "none !important",
                      borderRadius: "10px !important",
                      border: `2px solid ${
                        appSettings?.webCheckinButtonColor || "#19344FCC"
                      } !important`,
                    }
                  : undefined
              }
            >
              {(details?.customerType !== "COMPANY" &&
                (isNotItalian
                  ? currentSection === 4
                  : hasExistingCustomer
                  ? currentSection === 5
                  : currentSection === 4)) ||
              (details?.customerType === "COMPANY" &&
                (hasExistingCustomer
                  ? currentSection === 5
                  : currentSection === 2))
                ? translate("Confirm")
                : translate("Continue")}
            </Button>
          </Box>
        </form>
      </WebCheckInLayout>
      {openModal && (
        <WebCheckInPopUp
          openModal={openModal}
          modalMessage={modalMessage}
          onRequestClose={handleClose}
          isSmallScreen={isSmallScreen}
        />
      )}
    </>
  );
}
